import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, Loader2 } from 'lucide-react';
import { searchAddress } from '../../lib/utils/geocoding';

interface AddressSearchProps {
  onAddressSelect: (latitude: number, longitude: number, address: string) => void;
  placeholder?: string;
}

export function AddressSearch({ onAddressSelect, placeholder = "Enter an address or city..." }: AddressSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ lat: string; lon: string; display_name: string }>>([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    
    if (query.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      const results = await searchAddress(query);
      setSearchResults(results);
      setShowResults(results.length > 0);
      setSearching(false);
    }, 500);
  }, []);

  const handleSelect = useCallback((result: { lat: string; lon: string; display_name: string }) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    onAddressSelect(lat, lon, result.display_name);
    setSearchQuery(result.display_name);
    setShowResults(false);
    setSearchResults([]);
  }, [onAddressSelect]);

  return (
    <div className="relative" ref={resultsRef}>
      <label className="block text-sm font-medium text-content/70 mb-2">
        Search by Address (optional)
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-content/40" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-text focus:border-transparent"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-content/40" />
        )}
      </div>
      {showResults && searchResults.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {searchResults.map((result, idx) => (
            <button
              key={idx}
              onClick={() => handleSelect(result)}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-content border-b border-gray-100 last:border-b-0"
            >
              {result.display_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
